import React, { useEffect, useState } from 'react';
import { collection, doc, getDoc, limit, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import type { UserProfile } from '@types';
import { db, functions } from '../../firebaseConfig';
import SupportChatModal, { type SupportChatRecord } from './support/SupportChatModal';

type Notification = { id: string; type?: string; title: string; message: string; organizationName?: string; requestReference?: string; chatId?: string };

const ServiceRequestNotifications: React.FC<{ currentUser: UserProfile }> = ({ currentUser }) => {
    const [notification, setNotification] = useState<Notification | null>(null);
    const [openChat, setOpenChat] = useState<SupportChatRecord | null>(null);
    const [chatError, setChatError] = useState('');
    useEffect(() => onSnapshot(query(collection(db, 'management_notifications'), where('recipientUid', '==', currentUser.uid), where('read', '==', false), orderBy('createdAt', 'desc'), limit(1)), snapshot => {
        const document = snapshot.docs[0];
        setNotification(document ? { id: document.id, ...document.data() } as Notification : null);
    }, error => console.error('Service request notification failed loading.', error)), [currentUser.uid]);
    if (!notification && !openChat)
        return null;
    const dismiss = async () => {
        setNotification(null);
        try {
            await httpsCallable(functions, 'manageServiceRequests')({ action: 'markNotificationRead', notificationId: notification?.id });
        } catch (error)
            { console.error('Service request notification failed dismissing.', error); }
    };
    const showChat = async () => {
        if (!notification?.chatId)
            return;
        setChatError('');
        try {
            const snapshot = await getDoc(doc(db, 'support_chats', notification.chatId));
            if (!snapshot.exists())
                throw new Error('Conversation is no longer available.');
            setOpenChat({ id: snapshot.id, ...snapshot.data() } as SupportChatRecord);
            await dismiss();
        } catch (error: any)
            { setChatError(error.message || 'Conversation could not be opened.'); }
    };
    const label = notification?.type === 'consultant_assignment' ? 'Consultant Assignment'
        : notification?.type === 'survey_draft_ready' ? 'Survey Draft Ready'
            : notification?.type === 'service_evidence' ? 'Client Evidence'
                : notification?.type === 'support_chat' ? 'New Client Chat'
                    : notification?.type === 'helpdesk_ticket' ? 'New Helpdesk Ticket'
                        : notification?.type === 'service_request' ? 'New Service Request' : 'Client Notification';
    return <>
        {notification && <div className="fixed bottom-6 right-6 z-[200] w-[min(24rem,calc(100vw-3rem))] rounded-2xl border border-blue-400/30 bg-slate-900 p-5 text-white shadow-2xl"><div className="flex items-start justify-between gap-4"><div><p className="text-[10px] font-black uppercase tracking-widest text-blue-300">{label}</p><h3 className="mt-1 font-extrabold">{notification.title}</h3><p className="mt-2 line-clamp-3 text-sm text-slate-300">{notification.message}</p>{(notification.organizationName || notification.requestReference) && <p className="mt-2 text-xs font-semibold text-blue-300">{[notification.organizationName, notification.requestReference].filter(Boolean).join(' · ')}</p>}{notification.chatId && <button type="button" onClick={() => void showChat()} className="mt-3 text-xs font-bold text-blue-300">Open conversation →</button>}{chatError && <p role="alert" className="mt-2 text-xs text-red-300">{chatError}</p>}</div><button onClick={() => void dismiss()} aria-label="Dismiss notification" className="rounded-lg p-2 text-slate-400 hover:bg-slate-800 hover:text-white">×</button></div></div>}
        {openChat && <SupportChatModal mode="admin" chat={openChat} onClose={() => setOpenChat(null)} />}
    </>;
};

export default ServiceRequestNotifications;
